/**
 * The engine's only source of randomness.
 *
 * mulberry32, written with integer operations only: every intermediate is an
 * int32 or a uint32, produced by `Math.imul`, `^`, `>>>` and `|`. Those are
 * exactly specified by ECMAScript, so the stream is byte-identical on every
 * engine and every CPU — which is what BASELINE invariant 1 requires.
 *
 * `Math.random` is banned in this package by ESLint. There is no other entropy
 * source here: the same seed always replays the same game.
 */
import type { RngState } from './types.js';

/** Create a PRNG state from a seed. Any integer works; it is coerced to uint32. */
export function createRng(seed: number): RngState {
  return { seed: seed >>> 0 };
}

/** Copy a PRNG state. Used by `cloneGameState`. */
export function cloneRng(rng: RngState): RngState {
  return { seed: rng.seed >>> 0 };
}

/**
 * Advance the generator and return the next unsigned 32-bit word.
 *
 * mulberry32 as published, with the additions kept inside int32 range by the
 * trailing `^ t` (which applies ToInt32 to an exactly representable sum).
 */
export function nextUint32(rng: RngState): number {
  const a = (rng.seed + 0x6d2b79f5) | 0;
  rng.seed = a >>> 0;
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return (t ^ (t >>> 14)) >>> 0;
}

/**
 * A uniform integer in `[0, bound)`.
 *
 * The multiply-then-divide is exact: a uint32 times a bound below 2^21 is under
 * 2^53, so the product is an exact double, and dividing by 2^32 is an exact
 * exponent shift. No rounding happens anywhere, on any platform.
 *
 * Returns 0 for a bound of 1 or less, so callers never have to guard.
 */
export function nextInt(rng: RngState, bound: number): number {
  if (bound <= 1) {
    // Still draw, so the stream does not depend on the bound being degenerate.
    nextUint32(rng);
    return 0;
  }
  return Math.floor((nextUint32(rng) * bound) / 0x1_0000_0000);
}
